import { SapError } from 'src/app/lego-utils';
import type { ErrorEvent, EventHint } from '@sentry/angular';

const hasExceptionValueIncluding = (event: ErrorEvent, needle: string): boolean =>
  event.exception?.values?.some((value) => value.value?.includes(needle)) ?? false;

const hasExceptionValueEqualling = (event: ErrorEvent, message: string): boolean =>
  event.exception?.values?.some((value) => value.value === message) ?? false;

/**
 * Decides whether a Sentry error event should be reported. Returns the event to keep it, or
 * `null` to drop it (Sentry's `beforeSend` contract requires `null`, not `undefined`).
 *
 * Dropped cases are all benign or upstream noise that would otherwise obscure real EMMET bugs:
 * - SAP Gateway 5xx: upstream failures already surfaced to users via the Akita error store.
 * - MSAL `interaction_in_progress`: a benign race when MsalGuard and MsalInterceptor both start
 *   a redirect on a cold tab load. See https://the-lego-group.sentry.io/issues/EMMET-2
 * - MSAL `endpoints_resolution_error`: a transient network failure fetching OpenID endpoint
 *   metadata during a login redirect. The appended "untrusted_authority" detail is misleading —
 *   the authority is the standard Microsoft public cloud, trusted by MSAL by default, so it is
 *   not a config problem (a real mis-trust would fail every login, not a single one). MSAL
 *   already redirects the affected user to the loginFailedRoute (/error/login-failed); the
 *   rejected redirect promise should not be reported as an unhandled crash.
 *   See https://the-lego-group.sentry.io/issues/EMMET-D
 * - Optimizely `Instance closed`: the SDK registers its own pagehide/unload listener that calls
 *   client.close(), which rejects its internal cleanup-task deferreds with
 *   `Error('Instance closed')`. Once onReady() has settled, that rejection is orphaned and
 *   surfaces via Angular's global handler on routes that resolved a feature flag (e.g.
 *   /data/design). It is a benign teardown artefact that FeatureFlagService's rxjs pipeline
 *   cannot catch (it is the SDK's own internal promise). Matched exactly so a genuine error
 *   merely mentioning the phrase still reports. See https://the-lego-group.sentry.io/issues/EMMET-E
 */
export const filterSentryEvent = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const error = hint.originalException;

  const isSap5xx =
    error instanceof SapError && error.httpStatusCode !== undefined && error.httpStatusCode >= 500;

  const isInteractionInProgress = hasExceptionValueIncluding(event, 'interaction_in_progress');

  const isEndpointsResolutionError = hasExceptionValueIncluding(
    event,
    'endpoints_resolution_error'
  );

  const isOptimizelyInstanceClosed = hasExceptionValueEqualling(event, 'Instance closed');

  const shouldDrop =
    isSap5xx || isInteractionInProgress || isEndpointsResolutionError || isOptimizelyInstanceClosed;

  // eslint-disable-next-line no-null/no-null
  return shouldDrop ? null : event;
};
